import React from "react";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsOfService = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const isDark = theme === "dark";

  return (
    <section className="py-32 px-8">
      {/* Return Button */}
      <Link
        to="/"
        className={`
          fixed
          top-24
          left-8
          flex
          items-center
          gap-2
          px-4
          py-2
          rounded-lg
          transition-all
          duration-300
          border
          ${isDark 
            ? "text-gray-300 border-green-500/30 hover:border-green-400 hover:text-green-400 hover:bg-green-500/10" 
            : "text-gray-700 border-green-900/30 hover:border-green-800 hover:text-green-900 hover:bg-green-900/10"
          }
        `}
      >
        <ArrowLeft className="w-4 h-4" />
        {language === "en" ? "Back to Home" : "Retour à l'Accueil"}
      </Link>

      <div className="max-w-4xl mx-auto">
        {/* Title */}
        <h1 className={`
          text-4xl md:text-5xl lg:text-6xl 
          font-bold 
          text-center
          ${isDark 
            ? "bg-gradient-to-r from-green-400 via-green-500 to-green-400" 
            : "bg-gradient-to-r from-green-900 via-green-800 to-green-900"
          }
          bg-clip-text 
          text-transparent
          mb-16
        `}>
          {language === "en" ? "Terms of Service" : "Conditions d'Utilisation"}
        </h1>

        {/* Content Container */}
        <div className={`
          prose max-w-none
          ${isDark ? "prose-invert" : ""}
          ${isDark ? "text-gray-300" : "text-gray-700"}
        `}>
          {/* Last Updated */}
          <p className="text-sm mb-8">
            {language === "en" 
              ? "Last Updated: April 2024" 
              : "Dernière mise à jour : Avril 2024"}
          </p>

          {/* 1. Introduction */}
          <h2>
            {language === "en" 
              ? "1. Introduction" 
              : "1. Introduction"}
          </h2>
          <p>
            {language === "en"
              ? 'Welcome to Dynovant Systems. These Terms of Service ("Terms") govern your use of our website and the digital services we provide, including but not limited to automation solutions, AI-driven applications, data services, and systems integration (collectively, "Services"). By accessing or using our Services, you agree to be bound by these Terms.'
              : 'Bienvenue chez Dynovant Systems. Ces Conditions d\'Utilisation (« Conditions ») régissent votre utilisation de notre site web et des services numériques que nous fournissons, y compris, mais sans s\'y limiter, les solutions d\'automatisation, les applications basées sur l\'IA, les services de données et l\'intégration de systèmes (collectivement, les « Services »). En accédant ou en utilisant nos Services, vous acceptez d\'être lié par ces Conditions.'}
          </p>

          {/* 2. Acceptance of Terms */}
          <h2>
            {language === "en" 
              ? "2. Acceptance of Terms" 
              : "2. Acceptation des Conditions"}
          </h2>
          <p>
            {language === "en"
              ? "By accessing or using our Services, you confirm that you have read, understood, and agree to be legally bound by these Terms. If you do not agree with any part of these Terms, you must not use our Services."
              : "En accédant ou en utilisant nos Services, vous confirmez avoir lu, compris et accepté d'être légalement lié par ces Conditions. Si vous n'êtes pas d'accord avec une partie quelconque de ces Conditions, vous ne devez pas utiliser nos Services."}
          </p>

          {/* 3. Description of Services */}
          <h2>
            {language === "en" 
              ? "3. Description of Services" 
              : "3. Description des Services"}
          </h2>
          <p>
            {language === "en"
              ? "Dynovant Systems provides:"
              : "Dynovant Systems fournit :"}
          </p>
          <ul>
            <li>
              {language === "en"
                ? "Custom Software Development & Automation: Including solutions built with Python and No-Code platforms."
                : "Développement de Logiciels sur Mesure & Automatisation : Y compris des solutions construites avec Python et des plateformes No-Code."}
            </li>
            <li>
              {language === "en"
                ? "AI-Powered Systems: Such as chatbots, automated agents, and RAG systems."
                : "Systèmes Alimentés par l'IA : Tels que les chatbots, les agents automatisés et les systèmes RAG."}
            </li>
            <li>
              {language === "en"
                ? "Data Services: Including web scraping, data cleaning, analysis, and visualization."
                : "Services de Données : Y compris le web scraping, le nettoyage, l'analyse et la visualisation des données."}
            </li>
            <li>
              {language === "en"
                ? "Systems Integration: Connecting business tools and platforms to create streamlined workflows."
                : "Intégration de Systèmes : Connexion d'outils et de plateformes d'entreprise pour créer des flux de travail optimisés."}
            </li>
            <li>
              {language === "en"
                ? "Website & Dashboard Development: Covering web design, reporting dashboards, and business intelligence tools."
                : "Développement de Sites Web & Tableaux de Bord : Couvrant la conception web, les tableaux de bord de reporting et les outils de business intelligence."}
            </li>
            <li>
              {language === "en"
                ? "Digital Strategy Consultation & Business Process Optimization: To help businesses scale and improve efficiency."
                : "Consultation en Stratégie Digitale & Optimisation des Processus : Pour aider les entreprises à se développer et à améliorer leur efficacité."}
            </li>
          </ul>

          {/* 4. User Responsibilities */}
          <h2>
            {language === "en" 
              ? "4. User Responsibilities" 
              : "4. Responsabilités de l'Utilisateur"}
          </h2>
          <ul>
            <li>
              {language === "en"
                ? "Accurate Information: You agree to provide accurate and current information when requested."
                : "Informations Exactes : Vous acceptez de fournir des informations exactes et à jour lorsque demandé."}
            </li>
            <li>
              {language === "en"
                ? "Compliance: You will use our Services in compliance with all applicable laws, regulations, and these Terms."
                : "Conformité : Vous utiliserez nos Services en conformité avec toutes les lois, réglementations et ces Conditions applicables."}
            </li>
            <li>
              {language === "en"
                ? "Prohibited Conduct: You must not misuse our Services, interfere with their proper functioning, or engage in any fraudulent or harmful activities."
                : "Conduite Interdite : Vous ne devez pas faire un mauvais usage de nos Services, interférer avec leur bon fonctionnement ou vous engager dans des activités frauduleuses ou nuisibles."}
            </li>
          </ul>

          {/* 5. Intellectual Property Rights */}
          <h2>
            {language === "en" 
              ? "5. Intellectual Property Rights" 
              : "5. Droits de Propriété Intellectuelle"}
          </h2>
          <ul>
            <li>
              {language === "en"
                ? "Our IP: All content, designs, trademarks, logos, and proprietary technology used in our Services are the property of Dynovant Systems or its licensors and are protected by intellectual property laws."
                : "Notre PI : Tout le contenu, les designs, marques, logos et technologies propriétaires utilisés dans nos Services sont la propriété de Dynovant Systems ou de ses concédants et sont protégés par les lois sur la propriété intellectuelle."}
            </li>
            <li>
              {language === "en"
                ? "Limited License: We grant you a limited, non-exclusive, non-transferable license to access and use our Services solely for your internal business purposes."
                : "Licence Limitée : Nous vous accordons une licence limitée, non exclusive et non transférable pour accéder et utiliser nos Services uniquement à des fins commerciales internes."}
            </li>
            <li>
              {language === "en"
                ? "Restrictions: You may not copy, modify, distribute, or create derivative works from our content without our prior written consent."
                : "Restrictions : Vous ne pouvez pas copier, modifier, distribuer ou créer des œuvres dérivées de notre contenu sans notre consentement écrit préalable."}
            </li>
          </ul>

          {/* 6. Disclaimers */}
          <h2>
            {language === "en" 
              ? "6. Disclaimers" 
              : "6. Avertissements"}
          </h2>
          <ul>
            <li>
              {language === "en"
                ? 'Service "As Is": Our Services are provided on an "as is" and "as available" basis without warranties of any kind, either express or implied.'
                : 'Service "Tel Quel" : Nos Services sont fournis "tels quels" et "selon disponibilité" sans garanties d\'aucune sorte, expresses ou implicites.'}
            </li>
            <li>
              {language === "en"
                ? "No Guarantees: While we strive to provide high-quality, reliable services, we do not guarantee that the Services will be error-free or uninterrupted."
                : "Aucune Garantie : Bien que nous nous efforcions de fournir des services de haute qualité et fiables, nous ne garantissons pas que les Services seront exempts d'erreurs ou ininterrompus."}
            </li>
          </ul>

          {/* 7. Limitation of Liability */}
          <h2>
            {language === "en" 
              ? "7. Limitation of Liability" 
              : "7. Limitation de Responsabilité"}
          </h2>
          <p>
            {language === "en"
              ? "To the fullest extent permitted by law, Dynovant Systems shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of our Services, even if advised of the possibility of such damages. Our aggregate liability shall not exceed the fees paid by you for the use of our Services during the relevant period."
              : "Dans toute la mesure permise par la loi, Dynovant Systems ne sera pas responsable des dommages indirects, accessoires, spéciaux, consécutifs ou punitifs découlant de ou liés à votre utilisation de nos Services, même si nous avons été informés de la possibilité de tels dommages. Notre responsabilité totale ne dépassera pas les frais que vous avez payés pour l'utilisation de nos Services pendant la période concernée."}
          </p>

          {/* 8. Indemnification */}
          <h2>
            {language === "en" 
              ? "8. Indemnification" 
              : "8. Indemnisation"}
          </h2>
          <p>
            {language === "en"
              ? "You agree to indemnify and hold harmless Dynovant Systems, its affiliates, and their respective officers, directors, employees, and agents from any claims, liabilities, damages, or expenses (including legal fees) arising out of or related to your use of our Services, violation of these Terms, or infringement of any intellectual property or other rights."
              : "Vous acceptez d'indemniser et de dégager de toute responsabilité Dynovant Systems, ses affiliés et leurs dirigeants, administrateurs, employés et agents respectifs de toute réclamation, responsabilité, dommage ou dépense (y compris les frais juridiques) découlant de ou liés à votre utilisation de nos Services, à la violation de ces Conditions ou à la violation de toute propriété intellectuelle ou autres droits."}
          </p>

          {/* 9. Governing Law and Dispute Resolution */}
          <h2>
            {language === "en" 
              ? "9. Governing Law and Dispute Resolution" 
              : "9. Loi Applicable et Résolution des Litiges"}
          </h2>
          <p>
            {language === "en"
              ? "These Terms shall be governed by and construed in accordance with the laws of France and the UAE. Any disputes arising under these Terms shall be resolved through negotiation or, if necessary, binding arbitration or litigation in the courts of Paris, France or Dubai, UAE."
              : "Ces Conditions sont régies et interprétées conformément aux lois de la France et des EAU. Tout litige découlant de ces Conditions sera résolu par négociation ou, si nécessaire, par arbitrage contraignant ou litige devant les tribunaux de Paris, France ou Dubai, EAU."}
          </p>

          {/* 10. Termination */}
          <h2>
            {language === "en" 
              ? "10. Termination" 
              : "10. Résiliation"}
          </h2>
          <p>
            {language === "en"
              ? "We reserve the right to terminate or suspend your access to our Services at our discretion, without prior notice, if you violate these Terms or if we deem it necessary to protect our interests or those of other users."
              : "Nous nous réservons le droit de résilier ou de suspendre votre accès à nos Services à notre discrétion, sans préavis, si vous violez ces Conditions ou si nous le jugeons nécessaire pour protéger nos intérêts ou ceux des autres utilisateurs."}
          </p>

          {/* 11. Changes to These Terms */}
          <h2>
            {language === "en" 
              ? "11. Changes to These Terms" 
              : "11. Modifications de ces Conditions"}
          </h2>
          <p>
            {language === "en"
              ? "Dynovant Systems reserves the right to modify these Terms at any time. Any changes will be posted on our website, and your continued use of our Services following the posting of changes constitutes your acceptance of such changes."
              : "Dynovant Systems se réserve le droit de modifier ces Conditions à tout moment. Toute modification sera publiée sur notre site web, et votre utilisation continue de nos Services après la publication des modifications constitue votre acceptation de ces modifications."}
          </p>

          {/* 12. Contact Information */}
          <h2>
            {language === "en" 
              ? "12. Contact Information" 
              : "12. Informations de Contact"}
          </h2>
          <p>
            {language === "en"
              ? "If you have any questions about these Terms or our Services, please contact us at:"
              : "Si vous avez des questions concernant ces Conditions ou nos Services, veuillez nous contacter à :"}
          </p>
          <p className="mb-2">{language === "en" 
            ? "Email: contact@dynovant.com"
            : "Email : contact@dynovant.com"}
          </p>
          <p className="mb-2">{language === "en"
            ? "Address: Dubai, UAE / Paris, France"
            : "Adresse : Dubai, UAE / Paris, France"}
          </p>
        </div>
      </div>
    </section>
  );
};

export default TermsOfService; 